// frontend/src/pages/Menu.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "../ui/Page";
import MenuCard from "../ui/MenuCard";
import { Button, Modal } from "../ui/UIComponents";
import CartModal from "../ui/CartModal";
import SalesChatWidget from "./SalesChatWidget";

// --- IMPORT THE JSON FILE HERE ---
import HDSD_DATA from "./hdsd.json";

/** Departments (base list without SysAdmin; we'll append SysAdmin at runtime if admin) */
const BASE_DEPARTMENTS = [
  { id: 1, key: "sales", vi: "Phòng Kinh Doanh", en: "Sales Department" },
  { id: 2, key: "purchase", vi: "Phòng Mua Hàng", en: "Purchase Department" },
];

// SysAdmin department descriptor (conditionally injected)
const SYSADMIN_DEPT = { id: 3, key: "sysadmin", vi: "Web Tools", en: "System Administration" };

/** Draft data for dashboard preview (top 5 recent users) */
const TOP_USERS_DRAFT = [
  {
    rank: 1,
    email: "Super.man@example.com",
    lastLogin: "2024-12-07 09:15",
    totalUse: 42,
    totalDownload: 5,
    quotedWk: 2,
    totalQuoted: 10,
  },
  {
    rank: 2,
    email: "Wonder.woman@example.com",
    lastLogin: "2024-12-06 16:20",
    totalUse: 31,
    totalDownload: 3,
    quotedWk: 1,
    totalQuoted: 8,
  },
  {
    rank: 3,
    email: "Spider.man@example.com",
    lastLogin: "2024-12-05 11:02",
    totalUse: 24,
    totalDownload: 1,
    quotedWk: 0,
    totalQuoted: 5,
  },
  {
    rank: 4,
    email: "Iron.man@example.com",
    lastLogin: "2024-12-04 14:37",
    totalUse: 18,
    totalDownload: 0,
    quotedWk: 0,
    totalQuoted: 2,
  },
  {
    rank: 5,
    email: "Bat.man@example.com",
    lastLogin: "2024-12-03 08:10",
    totalUse: 12,
    totalDownload: 0,
    quotedWk: 0,
    totalQuoted: 0,
  },
];

function getEmailLocalPart(email) {
  if (!email) return "";
  const atIndex = email.indexOf("@");
  if (atIndex === -1) return email;
  return email.slice(0, atIndex);
}

function formatViDateTime(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    return new Intl.DateTimeFormat("vi-VN", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(d);
  } catch {
    return String(value);
  }
}

/** Tools per department */
const TOOLS = {
  sales: [
    {
      id: 1,
      vi: "Tư vấn loại SP phù hợp dựa trên chiều ngang và chiều cao cửa sổ",
      en: "",
      path: "/size-check",
    },
    {
      id: 2,
      vi: "Kiểm tra kích thước sản phẩm",
      en: "",
      path: "/quick-check",
    },
    {
      id: 3,
      vi: "Thông tin sản phẩm (tải file)",
      en: "Product Information",
      path: "/product-info",
    },
    {
      id: 4,
      vi: "Đào tạo SP HD / HD Product Training",
      en: "Muốn giỏi nhất trong bán hàng thì phải học hoài, luyện hoài",
      external: "https://hunterdouglasvn.com/training-main/",
    },
    {
      id: 5,
      vi: "Đào tạo SP ICT / Incanto Product Training",
      en: "Học thì chả thấy oai gì, nhưng không học thì lấy đâu ra đơn hàng",
      external: "https://daotao.incantovietnam.com.vn/",
    },
  ],
  purchase: [
    { id: 1, vi: "Xin giá SP", en: "Request Price", path: "/purchase/ewp" },
    { id: 2, vi: "Tìm kiếm nguồn gốc vải (HD)", en: "Fabric search", path: "/fabric-origin" },
    { id: 3, vi: "Thông tin sản phẩm (tải file)", en: "Product Information (download files)", path: "/product-info" },
    { id: 4, vi: "Chatbot", en: "Estimate Packing", path: "/purchase/packing-estimate" },
    { id: 5, vi: "Setup", en: "Configuration", path: "/purchase/config" },
    { id: 6, vi: "Web Email (tạm)", en: "Web Email (temp)", path: "/purchase/web-email" },
    { id: 7, vi: "Quản lý BOM", en: "BOM Management", path: "/purchase/bom" },
    { id: 8, vi: "Quản lý Đơn mua hàng", en: "Purchase Order Management", path: "/purchase/order-management" },
    { id: 9, vi: "Đơn mua hàng Thành Phẩm", en: "Finished Goods Purchase Order", path: "/purchase/finished-goods-order" },
  ],
  sysadmin: [
    { id: 1, vi: "New User", en: "Approve & Send OTP", path: "/sysadmin/access" },
    { id: 2, vi: "Quản lý Nhân sự", en: "Staff Maintenance", path: "/sysadmin/staff" },
  ],
};

export default function Menu() {
  const nav = useNavigate();

  /** ---------------- Session Gate (redirect to /login if not authenticated) ---------------- */
  const [gateReady, setGateReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/auth?action=me-user", { credentials: "include" });
        const j = await res.json().catch(() => ({}));
        const ok = !!(j && j.ok === true);
        if (!ok) {
          const next = `${window.location.pathname}${window.location.search || ""}`;
          window.location.replace(`/login?next=${encodeURIComponent(next)}`);
          return;
        }
        if (!cancelled) setGateReady(true);
      } catch {
        const next = `${window.location.pathname}${window.location.search || ""}`;
        window.location.replace(`/login?next=${encodeURIComponent(next)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** ---------------- Existing component state ---------------- */
  const [step, setStep] = useState("dept"); // 'dept' or 'tools'
  const [dept, setDept] = useState(null);
  const [selected, setSelected] = useState("");
  const inputRef = useRef(null);

  // ---------- Session & Admin state ----------
  const [authOk, setAuthOk] = useState(false); // purchase gate result
  const [authChecking, setAuthChecking] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);

  // ---------- Dashboard state ----------
  const [topUsers, setTopUsers] = useState([]);
  const [topUsersLoading, setTopUsersLoading] = useState(false);

  // ---------- Purchase pw modal ----------
  const [pwModal, setPwModal] = useState(false);

  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState(false);
  const pwRef = useRef(null);
  const nextActionRef = useRef(null);

  // ---------- Cart Modal State ----------
  const [cartOpen, setCartOpen] = useState(false);

  // ---------- HDSD Modal State ----------
  const [guideOpen, setGuideOpen] = useState(false);

  // --- Helpers ---
  async function apiAuthMeUser() {
    try {
      const r = await fetch("/api/auth?action=me-user", { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      const adminFlag = !!(j && (j.admin === true || j.is_admin === true || j.role === "admin"));
      setIsAdmin(adminFlag);
      setAdminChecked(true);
      return !!(j && j.ok);
    } catch {
      setAdminChecked(true);
      return false;
    }
  }

  async function apiPurchaseMe() {
    try {
      const r = await fetch("/api/auth?action=me", { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      return !!(j && j.ok === true);
    } catch {
      return false;
    }
  }

  async function apiPurchaseLogin(password) {
    const r = await fetch("/api/auth?action=login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password }),
    });
    const j = await r.json().catch(() => ({}));
    return !!(j && j.ok === true);
  }

  function requirePurchaseAuth(thenDo) {
    setAuthChecking(true);
    (async () => {
      const ok = await apiPurchaseMe();
      setAuthOk(ok);
      setAuthChecking(false);
      if (ok) {
        thenDo?.();
      } else {
        nextActionRef.current = thenDo;
        setPw("");
        setPwErr(false);
        setPwModal(true);
        setTimeout(() => pwRef.current?.focus(), 0);
      }
    })();
  }

  async function submitPw(e) {
    e.preventDefault();
    setPwErr(false);
    const ok = await apiPurchaseLogin(pw);
    if (!ok) {
      setPwErr(true);
      return;
    }
    setAuthOk(true);
    setPw("");
    setPwModal(false);
    const fn = nextActionRef.current;
    nextActionRef.current = null;
    fn?.();
  }

  // initial admin probe
  useEffect(() => {
    apiAuthMeUser();
  }, []);

  // fetch top users for dashboard when on department step and gate is ready
  useEffect(() => {
    if (!gateReady) return;
    if (step !== "dept") return;

    let cancelled = false;
    setTopUsersLoading(true);

    (async () => {
      try {
        const res = await fetch("/api/data?scope=search&action=top_users", {
          credentials: "include",
        });
        const j = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (j && j.ok && Array.isArray(j.items)) {
          const mapped = j.items.map((row, idx) => ({
            rank: idx + 1,
            email: row.user_name || "",
            lastLogin: row.last_use || null,
            totalUse: typeof row.total_use === "number" ? row.total_use : Number(row.total_use) || 0,
            // Map the new fields
            totalDownload: Number(row.total_download) || 0,
            quotedWk: Number(row.quoted_wk) || 0,
            totalQuoted: Number(row.total_quoted) || 0,
          }));
          setTopUsers(mapped);
        } else {
          setTopUsers([]);
        }
      } catch {
        if (!cancelled) {
          setTopUsers([]);
        }
      } finally {
        if (!cancelled) {
          setTopUsersLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gateReady, step]);

  const currentDepartments = useMemo(
    () => (isAdmin ? [...BASE_DEPARTMENTS, SYSADMIN_DEPT] : BASE_DEPARTMENTS),
    [isAdmin]
  );

  const currentList = useMemo(() => {
    if (step === "dept") return currentDepartments;
    if (!dept) return [];
    if (dept.key === "sysadmin" && !isAdmin) return [];
    return TOOLS[dept.key] || [];
  }, [step, dept, currentDepartments, isAdmin]);

  // ---------- Dashboard data (live + fallback) ----------
  const effectiveTopUsers =
    topUsers && topUsers.length ? topUsers : TOP_USERS_DRAFT;

  const maxUses = effectiveTopUsers.reduce((max, user) => {
    const uses = typeof user.totalUse === "number" ? user.totalUse : 0;
    return uses > max ? uses : max;
  }, 0);

  function chooseDept(d) {
    if (d.key === "sysadmin" && !isAdmin) {
      alert("Bạn không có quyền truy cập Phòng Quản Trị Hệ Thống / Forbidden");
      return;
    }
    if (d.key === "purchase") {
      requirePurchaseAuth(() => {
        nav(`/dept/${d.key}`);
        setDept(d);
        setStep("tools");
        setSelected("");
      });
      return;
    }
    nav(`/dept/${d.key}`);
    setDept(d);
    setStep("tools");
    setSelected("");
  }

  function trackUsage() {
    try {
      const url = "/api/data?scope=usage";
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify({})], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        // Fallback — fire and forget
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
          credentials: "include",
        }).catch(() => {});
      }
    } catch {
      // never block navigation on tracking error
    }
  }

  function openTool(t) {
    if (t.disabled) return;

    // External tools still count as "use"
    if (t.external) {
      trackUsage();
      window.open(t.external, "_blank");
      return;
    }

    if (dept?.key === "sysadmin" && !isAdmin) {
      alert("Bạn không có quyền truy cập mục này / Forbidden");
      return;
    }

    // Purchase-protected department
    if (dept?.key === "purchase" && !authOk) {
      requirePurchaseAuth(() => {
        trackUsage();
        nav(t.path);
      });
      return;
    }

    // Normal case: dept allowed → navigate & count as use
    trackUsage();
    nav(t.path);
  }

  // main selection handler used by keyboard + "Go" button + card click
  function handleEnter(num) {
    const item = currentList.find((m) => m.id === num);
    if (!item) return;
    if (step === "dept") {
      chooseDept(item);
    } else {
      openTool(item);
    }
  }

  // Global key handler
  useEffect(() => {
    if (!gateReady) return;
    const onKey = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "input" && document.activeElement !== inputRef.current) return;
      if (/^[0-9]$/.test(e.key)) {
        setSelected((prev) => (prev + e.key).replace(/^0+/, ""));
      } else if (e.key === "Enter" && selected) {
        handleEnter(Number(selected));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gateReady, selected, step, dept, currentList]);

  function goClick() {
    if (!selected) return;
    handleEnter(Number(selected));
  }

  function backToDept() {
    nav("/");
    setStep("dept");
    setDept(null);
    setSelected("");
  }

  const title =
    step === "dept"
      ? "Chọn phòng ban / Choose department"
      : `Công cụ — ${dept?.vi ?? ""} / Tools — ${dept?.en ?? ""}`;

  const helper =
    step === "dept"
      ? "Nhập số phòng ban và nhấn Enter / Type a department number and press Enter"
      : "Nhập số công cụ và nhấn Enter / Type a tool number and press Enter";

  // Determine which Guide section to show based on `step`
  const activeGuide = HDSD_DATA[step] || HDSD_DATA.default;

  return (
    <Page>
      <main className="menu-wrap">
        <h1 className="menu-title">Tuiss Web Tools — Main Menu</h1>

        {!gateReady ? (
          <>
            <h2 style={{ marginTop: -8 }}>Tuiss Web Tools</h2>
            <div className="box" style={{ marginTop: 12, opacity: 0.85 }}>
              Đang kiểm tra phiên đăng nhập… / Checking session…
            </div>
          </>
        ) : (
          <>
            <h2 style={{ marginTop: -8 }}>{title}</h2>

            {/* NAVBAR for Tools Step */}
            {step === "tools" && (
              <div
                style={{
                  margin: "8px 0 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* LEFT: Back + HDSD Guide */}
                <div>
                  <Button onClick={backToDept}>← Trở về / Back</Button>
                  <Button
                    onClick={() => setGuideOpen(true)}
                    style={{ marginLeft: 8 }}
                  >
                    HDSD
                  </Button>
                </div>

                {/* RIGHT: Cart */}
                <Button
                  onClick={() => setCartOpen(true)}
                  style={{ color: "#d32f2f", borderColor: "#d32f2f" }}
                >
                  Giỏ đơn hàng / My order cart
                </Button>
              </div>
            )}

            {step === "tools" && dept?.key === "purchase" && authChecking && (
              <div className="box" style={{ marginBottom: 12, opacity: 0.8 }}>
                Đang kiểm tra quyền truy cập… / Checking access…
              </div>
            )}

            {!adminChecked && (
              <div className="box" style={{ marginBottom: 12, opacity: 0.8 }}>
                Đang kiểm tra quyền quản trị… / Checking admin rights…
              </div>
            )}

            <div className="menu-grid">
              {currentList.map((item) => (
                <MenuCard
                  key={item.id}
                  id={item.id}
                  text={`${item.vi} / ${item.en}`}
                  disabled={!!item.disabled}
                  title={item.disabled ? "Coming soon" : ""}
                  onClick={() => handleEnter(item.id)}
                />
              ))}
            </div>

            <div className="menu-input">
              <label>
                {helper}:&nbsp;
                <input
                  ref={inputRef}
                  inputMode="numeric"
                  value={selected}
                  onChange={(e) =>
                    setSelected(e.target.value.replace(/\D+/g, ""))
                  }
                  placeholder={step === "dept" ? "1" : "1"}
                />
              </label>
              <Button onClick={goClick} style={{ marginLeft: 8 }}>
                Go
              </Button>
            </div>

{/* Dashboard Area: Chat Bot + Lean Status Panel */}
            {step === "dept" && (
              <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                
                {/* 1. Tiểu Mạnh Bot (Sales Assistant) */}
                <SalesChatWidget />

                {/* 2. Lean Dashboard (Compact Grid) */}
                <div className="menu-dashboard" style={{ padding: "10px 14px", background: "#fafafa", border: "1px solid #eee" }}>
                  <div className="menu-dashboard-boss" style={{ marginBottom: 10, color: "#d32f2f", fontWeight: "600" }}>
                    📢 Sếp HẠNH: ( ^‿^ )ノ "Cùng nhau chốt đơn mạnh mẽ hôm nay nhé!"
                  </div>

                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#888", marginBottom: 8 }}>
                    🏆 Top Chiến Binh (Hoạt động gần đây)
                  </div>

                  {/* Horizontal Grid Layout */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10 }}>
                    {effectiveTopUsers.slice(0, 4).map((user) => {
                      const name = getEmailLocalPart(user.email);
                      return (
                        <div key={user.email || user.rank} style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 10px", fontSize: "12px", boxShadow:"0 1px 2px rgba(0,0,0,0.03)" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom: 4 }}>
                            <span style={{ fontWeight: 700, color: "#333" }}>#{user.rank} {name}</span>
                            {user.rank === 1 && <span>👑</span>}
                          </div>
                          
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#555", marginTop: 2 }}>
                            <span>Total Use:</span> 
                            <b>{user.totalUse}</b>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#555", marginTop: 2 }}>
                            <span>Quoted:</span> 
                            <b>${user.totalQuoted ? user.totalQuoted.toLocaleString() : 0}</b>
                          </div>
                          
                          <div style={{ fontSize: "10px", color: "#aaa", marginTop: 6, textAlign:"right" }}>
                            {user.lastLogin ? formatViDateTime(user.lastLogin) : "-"}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 12, textAlign: "center" }}>
                    <Button onClick={() => setGuideOpen(true)} style={{ fontSize: 12, padding: "4px 10px", opacity: 0.8 }}>
                      📖 Xem Hướng Dẫn / User Guide
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* --- PASSWORD MODAL --- */}
            <Modal
              open={pwModal}
              title="Mật khẩu Phòng Mua Hàng / Purchase Department Password"
              onClose={() => {
                setPwModal(false);
                nextActionRef.current = null;
              }}
              maxWidth={460}
            >
              <div className="box" style={{ margin: 0 }}>
                <p style={{ marginTop: 0 }}>
                  Vui lòng nhập mật khẩu để tiếp tục / Enter password to continue.
                </p>
                <form onSubmit={submitPw} style={{ display: "grid", gap: 10 }}>
                  <label className="label">Mật khẩu / Password</label>
                  <input
                    ref={pwRef}
                    className="text-input"
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="••••••••"
                    autoFocus
                  />
                  {pwErr && <div style={{ color: "crimson" }}>Sai mật khẩu / Wrong password</div>}
                  <div className="actions">
                    <Button type="submit">Xác nhận / Confirm</Button>
                  </div>
                </form>
              </div>
            </Modal>
          </>
        )}
        
        {/* --- CART MODAL --- */}
        <CartModal open={cartOpen} onClose={() => setCartOpen(false)} />
        
        {/* --- GUIDE MODAL (Context-Aware) --- */}
        <Modal
          open={guideOpen}
          title={activeGuide.title}
          onClose={() => setGuideOpen(false)}
          maxWidth={600}
        >
           <div className="box" style={{ margin: 0, border: "none", padding: 0 }}>
              <div style={{ marginBottom: 16 }}>
                {activeGuide.intro}
              </div>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {activeGuide.steps.map((line, i) => (
                  <li key={i} style={{ marginBottom: 12 }}>
                    {line}
                  </li>
                ))}
              </ul>
           </div>
        </Modal>

      </main>
    </Page>
  );
}